// VinylWheel — flat-icon turntable matching the reference silhouette:
// white plinth with thick dark outline, dark charcoal disc, bright red centre
// label with a small spindle dot, stylised sound-wave arc grooves in two
// quadrants, and a fully outlined tonearm (hollow rectangular base, donut
// pivot) that swings from its parked "straight down" position onto the disc
// when the wheel spins.
import React, { useEffect, useMemo, useRef, useState } from "react";

// Style constants (single source of truth for the flat-icon palette)
const STROKE = "#3a3d42";       // dark grey outline used for plinth/disc
const TONEARM = "#7a7d82";      // medium grey for the tonearm
const FILL_BG = "#ffffff";      // plinth interior + hollow elements
const DISC = "#3a3d42";         // dark charcoal disc
const RED = "#e63946";          // bright red centre label

export default function VinylWheel({
  slices,
  spinning,
  targetIndex,
  onSpinEnd,
  onSpinClick,
  disabled = false,
  size = 380,
  centerTitle = "VINYL BAKU",
  centerSubtitle = "LUCKY COLLECTION",
}) {
  const [rotation, setRotation] = useState(0);
  const currentRotationRef = useRef(0);
  const finalized = useRef(false);

  const n = slices.length || 8;
  const sliceAngle = 360 / n;

  useEffect(() => {
    if (!spinning) return;
    if (typeof targetIndex !== "number" || targetIndex < 0) return;
    finalized.current = false;
    const centerDeg = sliceAngle * targetIndex + sliceAngle / 2;
    const currentMod = ((currentRotationRef.current % 360) + 360) % 360;
    const targetMod = (360 - centerDeg + 360) % 360;
    let delta = targetMod - currentMod;
    if (delta < 0) delta += 360;
    // More revolutions → visually a longer, more intense spin.
    // 12-15 full turns before landing on the winning slice.
    const spins = 12 + Math.floor(Math.random() * 4);
    const jitter = (Math.random() - 0.5) * (sliceAngle * 0.35);
    const finalRot = currentRotationRef.current + spins * 360 + delta + jitter;
    requestAnimationFrame(() => {
      currentRotationRef.current = finalRot;
      setRotation(finalRot);
    });
  }, [spinning, targetIndex, sliceAngle]);

  const onTransitionEnd = () => {
    if (spinning && !finalized.current) {
      finalized.current = true;
      onSpinEnd && onSpinEnd();
    }
  };

  // Number positions around the disc (viewBox 0..100)
  const numberPositions = useMemo(() => {
    return slices.map((s, i) => {
      const angle = sliceAngle * i + sliceAngle / 2 - 90;
      const r = 40;
      const x = 50 + r * Math.cos((angle * Math.PI) / 180);
      const y = 50 + r * Math.sin((angle * Math.PI) / 180);
      return { x, y, angle: angle + 90, number: s.number };
    });
  }, [slices, sliceAngle]);

  // Dimensions
  const plinthW = size;
  const plinthH = size * 0.82;
  const boxWidth = size * 1.05;
  const boxHeight = size * 1.05;
  const discSize = size * 0.72;
  const discLeft = (plinthW - discSize) / 2;
  const discTop = (plinthH - discSize) / 2;

  // Tonearm anchor geometry (in plinth viewBox 0..plinthW × 0..plinthH)
  const bracketX = plinthW * 0.86;
  const bracketY = plinthH * 0.09;
  const bracketW = plinthW * 0.08;
  const bracketH = plinthH * 0.14;
  const stemX = bracketX + bracketW / 2 - plinthW * 0.008;
  const stemY = bracketY + bracketH;
  const stemW = plinthW * 0.016;
  const stemH = plinthH * 0.048;
  const pivotX = bracketX + bracketW / 2;
  const pivotY = stemY + stemH + plinthH * 0.05;
  const pivotR = plinthH * 0.055;
  const pivotHoleR = plinthH * 0.028;

  // Arm target position on the disc (outer groove, top-right area)
  const discCX = discLeft + discSize / 2;
  const discCY = discTop + discSize / 2;
  const needleTargetX = discCX + discSize * 0.34;
  const needleTargetY = discCY + discSize * 0.05;
  const armAngleRad = Math.atan2(needleTargetY - pivotY, needleTargetX - pivotX);
  // Arm is stretched 25% beyond the on-disc needle target so it visually hangs
  // longer when parked (straight down) and reaches deeper across the disc when playing.
  const armLen = Math.hypot(needleTargetY - pivotY, needleTargetX - pivotX) * 1.50;
  const armEndX = pivotX + armLen * Math.cos(armAngleRad);
  const armEndY = pivotY + armLen * Math.sin(armAngleRad);
  const armAngleDeg = (armAngleRad * 180) / Math.PI;
  const armWidth = plinthW * 0.017;

  return (
    <div
      className="relative select-none"
      style={{ width: boxWidth, height: boxHeight, maxWidth: "100%" }}
      data-testid="vinyl-wheel"
    >
      {/* Plinth — white background with thick dark outline (flat icon style) */}
      <svg
        viewBox={`0 0 ${plinthW} ${plinthH}`}
        width={plinthW}
        height={plinthH}
        className="absolute"
        style={{ left: 0, top: 0, overflow: "visible" }}
      >
        <rect
          x={plinthW * 0.02}
          y={plinthH * 0.02}
          width={plinthW * 0.96}
          height={plinthH * 0.96}
          rx={plinthW * 0.025}
          fill={FILL_BG}
          stroke={STROKE}
          strokeWidth={plinthW * 0.018}
        />

      </svg>

      {/* Rotating disc — dark charcoal platter with red label */}
      <div
        className="absolute"
        style={{
          left: discLeft,
          top: discTop,
          width: discSize,
          height: discSize,
          transform: `rotate(${rotation}deg)`,
          // 6.8s spin with a strong deceleration curve — starts fast, slows to a stop.
          transition: spinning ? "transform 6.8s cubic-bezier(0.12, 0.72, 0.08, 1.0)" : "none",
          transformOrigin: "50% 50%",
          willChange: "transform",
        }}
        onTransitionEnd={onTransitionEnd}
      >
        <svg viewBox="0 0 100 100" width={discSize} height={discSize}>
          {/* Charcoal disc */}
          <circle cx="50" cy="50" r="49" fill={DISC} />

          {/* Numbers around the disc (cream, integrated with disc) */}
          {numberPositions.map((p, i) => (
            <g key={i} transform={`rotate(${p.angle}, ${p.x}, ${p.y})`}>
              <text
                x={p.x}
                y={p.y}
                fill="#f0e6d0"
                fontSize="4.5"
                fontFamily="'Playfair Display', serif"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ letterSpacing: "0.06em" }}
                opacity="0.95"
              >
                {p.number}
              </text>
            </g>
          ))}

          {/* Red centre label */}
          <circle cx="50" cy="50" r="16" fill={RED} />

          {/* Small white spindle dot in the very centre */}
          <circle cx="50" cy="50" r="1.6" fill="#ffffff" />

          {/* Label text (rotates with disc) */}
          <text
            x="50" y="41"
            fill="#ffffff"
            fontSize="2.3"
            textAnchor="middle"
            fontFamily="'Playfair Display', serif"
            fontWeight="700"
            style={{ letterSpacing: "0.22em" }}
            opacity="0.92"
          >
            {centerTitle}
          </text>
          <text
            x="50" y="58"
            fill="#ffffff"
            fontSize="1.5"
            textAnchor="middle"
            fontFamily="'Inter', sans-serif"
            fontWeight="600"
            style={{ letterSpacing: "0.20em" }}
            opacity="0.85"
          >
            {centerSubtitle}
          </text>
        </svg>
      </div>

      {/* Tonearm — the CIRCLE (pivot donut) is FIXED; the BRACKET + STEM + ARM
          + HEADSHELL all rotate together around the pivot's centre. */}
      <svg
        viewBox={`0 0 ${plinthW} ${plinthH}`}
        width={plinthW}
        height={plinthH}
        className="absolute pointer-events-none"
        style={{ left: 0, top: 0, overflow: "visible" }}
      >
        {/* ROTATING group (around pivot centre): bracket + stem + arm + headshell */}
        <g
          style={{
            transformOrigin: `${pivotX}px ${pivotY}px`,
            transformBox: "view-box",
            transform: spinning ? "rotate(45deg)" : "rotate(0deg)",
            transition: "transform 1.4s cubic-bezier(0.34, 1.28, 0.64, 1)",
          }}
        >
          {/* Bracket (hollow outlined rectangle above the pivot) */}
          <rect
            x={bracketX}
            y={bracketY}
            width={bracketW}
            height={bracketH}
            rx={plinthW * 0.006}
            fill={FILL_BG}
            stroke={TONEARM}
            strokeWidth={plinthW * 0.014}
          />
          {/* Stem connecting bracket to pivot */}
          <rect x={stemX} y={stemY} width={stemW} height={stemH} fill={TONEARM} />

          {/* Straight arm rectangle — drawn STRAIGHT DOWN from the pivot */}
          <rect
            x={pivotX - armWidth / 2}
            y={pivotY}
            width={armWidth}
            height={armLen}
            fill={TONEARM}
          />

          {/* Headshell — elongated rounded rectangle along the arm axis
              + small finger-lift tab sticking to the RIGHT (perpendicular). */}
          <g transform={`translate(${pivotX}, ${pivotY + armLen})`}>
            {/* Main headshell body — elongated (taller than wide in arm axis) */}
            <rect
              x={-plinthW * 0.030}
              y={-plinthW * 0.008}
              width={plinthW * 0.060}
              height={plinthW * 0.084}
              rx={plinthW * 0.008}
              fill={TONEARM}
            />
            {/* Small finger-lift tab protruding to the RIGHT of the headshell */}
            <rect
              x={plinthW * 0.030}
              y={plinthW * 0.020}
              width={plinthW * 0.036}
              height={plinthW * 0.014}
              rx={plinthW * 0.003}
              fill={TONEARM}
            />
          </g>
        </g>

        {/* FIXED: hollow donut pivot (rendered ON TOP so it stays perfectly centred) */}
        <circle cx={pivotX} cy={pivotY} r={pivotR} fill={FILL_BG} stroke={TONEARM} strokeWidth={plinthW * 0.014} />
        <circle cx={pivotX} cy={pivotY} r={pivotHoleR} fill={TONEARM} />
      </svg>

      {/* START — animated black circle button at bottom-left of plinth */}
      <style>
        {`
          @keyframes vw-start-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.25); }
            50%      { box-shadow: 0 0 0 8px rgba(0, 0, 0, 0), 0 6px 18px rgba(0, 0, 0, 0.30); }
          }
          @keyframes vw-start-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          .vw-start-btn:not(:disabled):hover {
            transform: translateY(-2px) scale(1.06);
            background: #111 !important;
          }
          .vw-start-btn:not(:disabled):active {
            transform: translateY(0) scale(0.98);
          }
        `}
      </style>
      <button
        onClick={onSpinClick}
        disabled={disabled || spinning}
        className="vw-start-btn absolute z-30 flex items-center justify-center transition-transform"
        style={{
          left: `${plinthW * 0.06}px`,
          top: `${plinthH * 0.76}px`,
          width: `${plinthH * 0.15}px`,
          height: `${plinthH * 0.15}px`,
          borderRadius: "9999px",
          background: (disabled || spinning) ? "#666" : "#000000",
          color: "#ffffff",
          border: "none",
          cursor: (disabled || spinning) ? "not-allowed" : "pointer",
          letterSpacing: "0.14em",
          fontSize: `${plinthH * 0.024}px`,
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
          opacity: disabled ? 0.5 : 1,
          animation: spinning
            ? `vw-start-spin 1.4s linear infinite`
            : (disabled ? "none" : `vw-start-pulse 2.2s ease-in-out infinite`),
          transitionDuration: "0.2s",
        }}
        data-testid="vinyl-wheel-spin"
        title={spinning ? "Fırlanır..." : "Çarxı fırlat"}
      >
        {spinning ? (
          <svg width="30%" height="30%" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2.5" strokeDasharray="14 42" strokeLinecap="round" />
          </svg>
        ) : "START"}
      </button>
    </div>
  );
}
